import { type FC, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import type { TaskSnapshot } from "../../types";

import { formatUsd, getUsageSummaryTotalCostUsd } from "../../utils/currency";
import {
  type FlowCategory,
  FLOW_CATEGORY_CONFIG,
} from "../../utils/taskFlowGrouping";
import {
  getCallbackStatus,
  getProviderColors,
  getStatusDot,
  getTaskProvider,
  isSchemaInvalid,
} from "../../utils/taskStatus";
import { formatTaskTypeSuffixLabel } from "../../utils/taskTypeLabel";
import { formatRelativeTime } from "../../utils/time";
import { ActivePulseIndicator } from "../common/ActivePulseIndicator";
import { DataTable } from "../common/DataTable";
import { isKnownProvider, ProviderIcon } from "../common/ProviderIcon";
import { CallbackStatusBadge, SchemaInvalidBadge } from "../common/TaskBadges";
import { Text } from "../common/Text";

interface FlowTaskCardProps {
  task: TaskSnapshot;
  flowCategory: FlowCategory;
  colSpan?: number;
}

export const FlowTaskCard: FC<FlowTaskCardProps> = ({
  task,
  flowCategory,
  colSpan = 7,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTaskId = searchParams.get("taskId");
  const isSelected = selectedTaskId === task.task_id;

  const callbackStatus = useMemo(() => getCallbackStatus(task), [task]);
  const totalCostUsd =
    task.cost_usd ?? getUsageSummaryTotalCostUsd(task.usage_summary ?? null);
  const hasCost =
    typeof totalCostUsd === "number" &&
    Number.isFinite(totalCostUsd) &&
    totalCostUsd > 0;

  const typeSuffix = formatTaskTypeSuffixLabel(task.type);
  const dot = getStatusDot(task.status);
  const rawProvider = getTaskProvider(task);
  const provider =
    rawProvider && isKnownProvider(rawProvider) ? rawProvider : null;
  const providerColors = getProviderColors(rawProvider);
  const config = FLOW_CATEGORY_CONFIG[flowCategory];
  const schemaInvalid = isSchemaInvalid(task);
  const issueKey = task.linked_ticket_id?.trim() ?? "";

  const handleClick = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("taskId", task.task_id);
      return next;
    });
  };

  return (
    <DataTable.SubRow
      className={`cursor-pointer ${isSelected ? "bg-slate-50 shadow-[inset_3px_0_0_0_rgb(16_185_129)] dark:bg-emerald-500/5 dark:shadow-[inset_3px_0_0_0_rgb(52_211_153)]" : ""}`}
      style={{ boxShadow: isSelected ? undefined : config.accentShadow }}
      onClick={handleClick}
    >
      <DataTable.Cell colSpan={colSpan}>
        <div className="flex min-w-0 items-center gap-x-2 sm:gap-x-3">
          {/* Status indicator — provider icon with pulsing border, or plain dot */}
          {(() => {
            if (dot.pulse) {
              return (
                <ActivePulseIndicator
                  size={provider ? "lg" : "sm"}
                  className="flex-shrink-0"
                  colorClassName={providerColors.pulseColor}
                  radarClassName={providerColors.radarColor}
                >
                  {provider && (
                    <ProviderIcon
                      provider={provider}
                      className="relative h-full w-full"
                    />
                  )}
                </ActivePulseIndicator>
              );
            }
            return (
              <span
                className={`h-2 w-2 flex-shrink-0 rounded-none ${dot.color}`}
              />
            );
          })()}

          {/* Type suffix */}
          <span className="min-w-0 flex-1 truncate font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-slate-600 dark:text-slate-300">
            {typeSuffix}
          </span>

          {/* Issue */}
          {issueKey && (
            <span className="hidden flex-shrink-0 items-center gap-1 text-xs text-slate-500 sm:flex dark:text-slate-400">
              <span className="font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400 dark:text-slate-500">
                Issue
              </span>
              <Text variant="mono">{issueKey}</Text>
            </span>
          )}

          {/* Cost */}
          {hasCost && (
            <Text className="flex-shrink-0 whitespace-nowrap">
              {formatUsd(totalCostUsd)}
            </Text>
          )}

          {/* Schema invalid indicator */}
          {schemaInvalid && <SchemaInvalidBadge compact />}

          {/* Callback badge */}
          {callbackStatus && (
            <CallbackStatusBadge status={callbackStatus} compact />
          )}

          {/* Updated time */}
          <span className="hidden flex-shrink-0 whitespace-nowrap text-xs text-slate-400 sm:inline dark:text-slate-500">
            {formatRelativeTime(task.updated_at)}
          </span>

          {/* Chevron */}
          <svg
            className="h-4 w-4 flex-shrink-0 text-slate-400 transition-colors group-hover:text-emerald-700 dark:group-hover:text-emerald-300"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </div>
      </DataTable.Cell>
    </DataTable.SubRow>
  );
};
